/**
 * StudioDetailPage - Admin read-only detail view for a single studio
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  getAdminStudio,
  updateAdminContact,
  createAdminContact,
  deleteAdminStudio,
  updateAdminStudio,
  sendStudioActivationEmail,
  formatCurrency,
  type AdminStudioDetail,
} from '../../../api/admin';
import { formatPhoneDisplay, normalizePhoneForStore } from '../../../utils/phone';
import { PlacesAutocomplete, type PlaceResult } from '../../../components/events/PlacesAutocomplete';
import { US_STATES } from '../../../utils/usStates';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import WebsiteVisitsCard from './WebsiteVisitsCard';

type Contact = AdminStudioDetail['contacts'][number];

interface EditContactForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  title: string;
}

interface AddContactForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  title: string;
  is_primary: boolean;
}

export default function StudioDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Delete Studio (SDA-138)
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Send Activation Email (dormant studios only, SDA-135)
  const [showActivationConfirm, setShowActivationConfirm] = useState(false);
  const [isSendingActivation, setIsSendingActivation] = useState(false);
  const [activationStatus, setActivationStatus] = useState<
    { type: 'success' | 'error'; message: string } | null
  >(null);

  // Edit Studio info
  const [showEditStudio, setShowEditStudio] = useState(false);
  const [isSavingStudio, setIsSavingStudio] = useState(false);
  const [editStudioError, setEditStudioError] = useState<string | null>(null);
  const [editStudioForm, setEditStudioForm] = useState({
    name: '', address_line1: '', city: '', state: '', zip_code: '', phone: '', website: '',
    google_place_id: '',
  });

  const [studio, setStudio] = useState<AdminStudioDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [editForm, setEditForm] = useState<EditContactForm>({ first_name: '', last_name: '', email: '', phone: '', title: '' });
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [saveContactError, setSaveContactError] = useState<string | null>(null);

  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [addForm, setAddForm] = useState<AddContactForm>({ first_name: '', last_name: '', email: '', phone: '', title: '', is_primary: false });
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [addContactError, setAddContactError] = useState<string | null>(null);

  // SDA-208: promote a non-primary contact to Primary (confirm-gated).
  const [makePrimaryContact, setMakePrimaryContact] = useState<Contact | null>(null);

  useEffect(() => {
    if (id) {
      loadStudio(id);
    }
  }, [id]);

  const loadStudio = async (studioId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAdminStudio(studioId);
      setStudio(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load studio');
    } finally {
      setIsLoading(false);
    }
  };

  const openAddContact = () => {
    setAddForm({ first_name: '', last_name: '', email: '', phone: '', title: '', is_primary: false });
    setAddContactError(null);
    setIsAddContactOpen(true);
  };

  const openEditStudio = () => {
    if (!studio) return;
    setEditStudioForm({
      name: studio.name || '',
      address_line1: studio.address_line1 || '',
      city: studio.city || '',
      state: studio.state || '',
      zip_code: studio.zip_code || '',
      phone: studio.phone ? formatPhoneDisplay(studio.phone) : '',
      website: studio.website || '',
      google_place_id: '',
    });
    setEditStudioError(null);
    setShowEditStudio(true);
  };

  const handleSaveStudio = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setEditStudioError(null);
    if (!editStudioForm.name.trim() || !editStudioForm.zip_code.trim()) {
      setEditStudioError('Studio name and ZIP code are required.');
      return;
    }
    try {
      setIsSavingStudio(true);
      await updateAdminStudio(id, {
        name: editStudioForm.name.trim(),
        address_line1: editStudioForm.address_line1 || null,
        city: editStudioForm.city || null,
        state: editStudioForm.state || null,
        zip_code: editStudioForm.zip_code.trim(),
        phone: editStudioForm.phone || null,
        website: editStudioForm.website || null,
        google_place_id: editStudioForm.google_place_id || undefined,
      });
      setShowEditStudio(false);
      await loadStudio(id);
    } catch (err) {
      setEditStudioError(err instanceof Error ? err.message : 'Failed to update studio');
    } finally {
      setIsSavingStudio(false);
    }
  };

  const handleDeleteStudio = async () => {
    if (!id || !studio) return;
    setDeleteError(null);
    try {
      setIsDeleting(true);
      await deleteAdminStudio(id);
      navigate('/admin/studios');
    } catch (err) {
      // 409 (has bookings/etc) and other errors surface here.
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete studio');
      setIsDeleting(false);
    }
  };

  const handleSendActivation = async () => {
    if (!id) return;
    setShowActivationConfirm(false);
    setActivationStatus(null);
    setIsSendingActivation(true);
    try {
      const result = await sendStudioActivationEmail(id);
      // The endpoint returns 200 even when the email provider soft-fails
      // (email_sent: false) — branch on the flag so we never claim a send
      // that didn't happen.
      if (result.email_sent) {
        setActivationStatus({
          type: 'success',
          message: `Activation email sent to ${result.recipient}.`,
        });
      } else {
        setActivationStatus({
          type: 'error',
          message: `Couldn't send the activation email to ${result.recipient}. Please try again or check the email provider configuration.`,
        });
      }
    } catch (err) {
      setActivationStatus({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to send activation email',
      });
    } finally {
      setIsSendingActivation(false);
    }
  };

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setIsAddingContact(true);
    setAddContactError(null);
    try {
      const newContact = await createAdminContact(id, {
        first_name: addForm.first_name.trim(),
        last_name: addForm.last_name.trim(),
        email: addForm.email.trim(),
        phone: addForm.phone.trim() || null,
        title: addForm.title.trim() || null,
        is_primary: addForm.is_primary,
      });
      setStudio(prev => {
        if (!prev) return prev;
        const updatedContacts = addForm.is_primary
          ? prev.contacts.map(c => ({ ...c, is_primary: false }))
          : prev.contacts;
        return { ...prev, contacts: [...updatedContacts, newContact] };
      });
      setIsAddContactOpen(false);
    } catch (err) {
      setAddContactError(err instanceof Error ? err.message : 'Failed to add contact');
    } finally {
      setIsAddingContact(false);
    }
  };

  const openEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setEditForm({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      phone: contact.phone ? formatPhoneDisplay(contact.phone) : '',
      title: contact.title ?? '',
    });
    setSaveContactError(null);
  };

  const handleSaveContact = async () => {
    if (!editingContact) return;
    setIsSavingContact(true);
    setSaveContactError(null);
    try {
      const rawPhone = normalizePhoneForStore(editForm.phone);
      await updateAdminContact(editingContact.id, {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        email: editForm.email,
        phone: rawPhone || null,
        title: editForm.title || null,
      });
      setStudio((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          contacts: prev.contacts.map((c) =>
            c.id === editingContact.id
              ? {
                  ...c,
                  first_name: editForm.first_name,
                  last_name: editForm.last_name,
                  email: editForm.email,
                  phone: rawPhone || null,
                  title: editForm.title || null,
                }
              : c
          ),
        };
      });
      setEditingContact(null);
    } catch (err) {
      setSaveContactError(err instanceof Error ? err.message : 'Failed to save contact');
    } finally {
      setIsSavingContact(false);
    }
  };

  // SDA-208: promote a contact to Primary. Only `is_primary: true` is ever sent;
  // the DB trigger `ensure_single_primary_contact` demotes the previous primary,
  // so exactly one primary always remains. Optimistically move the badge, revert on failure.
  const handleMakePrimary = async (contact: Contact) => {
    setMakePrimaryContact(null);
    const previous = studio?.contacts ?? null;
    setActivationStatus(null);
    setStudio(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        contacts: prev.contacts.map(c => ({ ...c, is_primary: c.id === contact.id })),
      };
    });
    try {
      await updateAdminContact(contact.id, { is_primary: true });
      setActivationStatus({
        type: 'success',
        message: `${contact.first_name} ${contact.last_name} is now the primary contact. Booking and inquiry emails will route to them.`,
      });
    } catch (err) {
      setStudio(prev => (prev && previous ? { ...prev, contacts: previous } : prev));
      setActivationStatus({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to update primary contact',
      });
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return '--';
    const dateStr = date.includes('T') ? date : date + 'T12:00:00';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const statusBadgeClass = (status: string) => {
    const styles: Record<string, string> = {
      pending_deposit: 'bg-warning-100 text-warning-700',
      pending_balance: 'bg-primary-100 text-primary-700',
      confirmed: 'bg-success-100 text-success-700',
      completed: 'bg-warm-100 text-warm-600',
      cancelled: 'bg-danger-100 text-danger-700',
    };
    return `px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-warm-100 text-warm-600'}`;
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-warm-200 rounded w-1/4 mb-4" />
        <div className="h-4 bg-warm-200 rounded w-1/2 mb-8" />
        <div className="space-y-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border p-6">
              <div className="h-6 bg-warm-100 rounded w-1/3 mb-4" />
              <div className="space-y-3">
                <div className="h-4 bg-warm-100 rounded w-2/3" />
                <div className="h-4 bg-warm-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error && !studio) {
    return (
      <div className="bg-danger-50 border border-danger-200 rounded-lg p-6 text-center">
        <p className="text-danger-700">{error}</p>
        <button
          onClick={() => id && loadStudio(id)}
          className="mt-4 px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 min-h-[44px]"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Not found
  if (!studio) {
    return (
      <div className="bg-warm-50 border border-warm-200 rounded-lg p-6 text-center">
        <p className="text-warm-600">Studio not found</p>
        <Link
          to="/admin/studios"
          className="mt-4 inline-block px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 min-h-[44px]"
        >
          Back to Studios
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <Link
          to="/admin/studios"
          className="text-warm-600 hover:text-warm-900 transition-colors self-start mt-1"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <div className="flex items-center gap-2 text-sm text-warm-500 mb-1">
            <Link to="/admin/studios" className="hover:text-warm-700">
              Studios
            </Link>
            <span>/</span>
            <span className="text-warm-700">Detail</span>
          </div>
          <h1 className="text-2xl font-bold text-warm-900">{studio.name}</h1>
          <div className="flex items-center gap-2 mt-2">
            {/* SDA-281: Active = the studio activated an account. Reading is_dormant (not
                is_active, which is always true) keeps this in lockstep with the
                "Send Activation Email" button below — Inactive ⇔ the button is offered. */}
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                !studio.is_dormant
                  ? 'bg-success-100 text-success-700'
                  : 'bg-warm-100 text-warm-600'
              }`}
            >
              {!studio.is_dormant ? 'Active' : 'Inactive'}
            </span>
            {studio.is_verified && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-700">
                Verified
              </span>
            )}
          </div>
        </div>
        <div className="ml-auto self-start flex items-center gap-3">
          {studio.is_dormant && studio.primary_contact && (
            <button
              onClick={() => { setActivationStatus(null); setShowActivationConfirm(true); }}
              disabled={isSendingActivation}
              className="px-4 py-2 text-sm font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSendingActivation ? 'Sending...' : 'Send Activation Email'}
            </button>
          )}
          <button
            onClick={() => { setDeleteConfirmText(''); setDeleteError(null); setShowDelete(true); }}
            className="px-4 py-2 text-sm font-medium text-danger-600 border border-danger-200 rounded-lg hover:bg-danger-50 transition-colors"
          >
            Delete Studio
          </button>
        </div>
      </div>

      {/* Activation email send status (SDA-135) */}
      {activationStatus && (
        <div
          role="alert"
          className={`mb-6 p-4 rounded-lg border text-sm ${
            activationStatus.type === 'success'
              ? 'bg-success-50 border-success-200 text-success-700'
              : 'bg-danger-50 border-danger-200 text-danger-700'
          }`}
        >
          {activationStatus.message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Studio Information */}
          <div className="bg-white rounded-xl shadow-sm border border-warm-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-warm-900">Studio Information</h2>
              <button
                onClick={openEditStudio}
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                Edit
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-warm-500">Address</p>
                <p className="text-warm-900">
                  {studio.address_line1
                    ? `${studio.address_line1}, ${studio.city || ''}, ${studio.state || ''} ${studio.zip_code}`
                    : '--'}
                </p>
              </div>
              <div>
                <p className="text-sm text-warm-500">Phone</p>
                {studio.phone ? (
                  <a href={`tel:${studio.phone}`} className="text-primary-600 hover:text-primary-700 min-h-[44px] inline-flex items-center">
                    {studio.phone}
                  </a>
                ) : (
                  <p className="text-warm-900">--</p>
                )}
              </div>
              <div>
                <p className="text-sm text-warm-500">Website</p>
                {studio.website ? (
                  <a
                    href={studio.website.startsWith('http') ? studio.website : `https://${studio.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 min-h-[44px] inline-flex items-center"
                  >
                    {studio.website}
                  </a>
                ) : (
                  <p className="text-warm-900">--</p>
                )}
              </div>
              <div>
                <p className="text-sm text-warm-500">Students</p>
                <p className="text-warm-900">{studio.student_count ?? '--'}</p>
              </div>
              <div>
                <p className="text-sm text-warm-500">Year Established</p>
                <p className="text-warm-900">{studio.year_established ?? '--'}</p>
              </div>
            </div>
          </div>

          {/* Contacts */}
          <div className="bg-white rounded-xl shadow-sm border border-warm-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-warm-900">Contacts</h2>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warm-100 text-warm-600">
                  {studio.contacts.length}
                </span>
              </div>
              <button
                type="button"
                onClick={openAddContact}
                className="px-3 py-1.5 text-sm font-medium text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors min-h-[36px]"
              >
                Add Contact
              </button>
            </div>
            {studio.contacts.length === 0 ? (
              <p className="text-warm-500">No contacts found</p>
            ) : (
              <div className="space-y-4">
                {studio.contacts.map((contact) => (
                  <div key={contact.id} className="border-b border-warm-100 pb-4 last:border-0 last:pb-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-warm-900">
                          {contact.first_name} {contact.last_name}
                        </p>
                        {contact.is_primary && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                            Primary
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!contact.is_primary && (
                          <button
                            type="button"
                            onClick={() => setMakePrimaryContact(contact)}
                            className="px-2.5 py-1 text-xs font-medium border border-primary-300 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                          >
                            Make Primary
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => openEditContact(contact)}
                          className="px-2.5 py-1 text-xs font-medium border border-warm-300 text-warm-600 rounded-lg hover:bg-warm-50 transition-colors"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                    {contact.title && (
                      <p className="text-sm text-warm-500">{contact.title}</p>
                    )}
                    <a
                      href={`mailto:${contact.email}`}
                      className="text-sm text-primary-600 hover:text-primary-700 min-h-[44px] inline-flex items-center"
                    >
                      {contact.email}
                    </a>
                    {contact.phone && (
                      <div>
                        <a
                          href={`tel:${contact.phone}`}
                          className="text-sm text-primary-600 hover:text-primary-700 min-h-[44px] inline-flex items-center"
                        >
                          {contact.phone}
                        </a>
                      </div>
                    )}
                    {/* Email preference toggles */}
                    <div className="mt-3 p-3 bg-warm-50 rounded-lg border border-warm-100">
                      <p className="text-xs font-semibold text-warm-700 mb-2">Email Preferences</p>
                      <div className="flex flex-wrap items-center gap-3">
                        {([
                          { key: 'receives_marketing_emails' as const, label: 'Marketing', value: contact.receives_marketing_emails },
                          { key: 'receives_booking_emails' as const, label: 'Booking', value: contact.receives_booking_emails },
                          { key: 'receives_reminder_emails' as const, label: 'Reminder', value: contact.receives_reminder_emails },
                        ]).map(({ key, label, value }) => (
                          <button
                            key={key}
                            type="button"
                            onClick={async () => {
                              setStudio(prev => {
                                if (!prev) return prev;
                                return { ...prev, contacts: prev.contacts.map(c => c.id === contact.id ? { ...c, [key]: !value } : c) };
                              });
                              try {
                                await updateAdminContact(contact.id, { [key]: !value });
                              } catch {
                                setStudio(prev => {
                                  if (!prev) return prev;
                                  return { ...prev, contacts: prev.contacts.map(c => c.id === contact.id ? { ...c, [key]: value } : c) };
                                });
                              }
                            }}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                              value
                                ? 'bg-success-50 border-success-200 text-success-700 hover:bg-success-100'
                                : 'bg-warm-100 border-warm-200 text-warm-500 hover:bg-warm-200'
                            }`}
                          >
                            <span className={`w-2 h-2 rounded-full ${value ? 'bg-success-500' : 'bg-warm-400'}`} />
                            {label}
                          </button>
                        ))}
                        {contact.bounce_unsubscribe && (
                          <span
                            title={`Auto-unsubscribed ${new Date(contact.bounce_unsubscribe.at).toLocaleDateString()} — ${contact.bounce_unsubscribe.reason}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-warning-50 border border-warning-200 text-warning-700"
                          >
                            <span className="w-2 h-2 rounded-full bg-warning-500" />
                            Auto-unsubscribed · bounced
                          </span>
                        )}
                      </div>

                      {/* Unsubscribe / Resubscribe all button */}
                      {(() => {
                        const allOff = !contact.receives_marketing_emails && !contact.receives_booking_emails && !contact.receives_reminder_emails;
                        return (
                          <button
                            type="button"
                            onClick={async () => {
                              const newValue = allOff;
                              setStudio(prev => {
                                if (!prev) return prev;
                                return {
                                  ...prev,
                                  contacts: prev.contacts.map(c =>
                                    c.id === contact.id
                                      ? { ...c, receives_marketing_emails: newValue, receives_booking_emails: newValue, receives_reminder_emails: newValue }
                                      : c
                                  ),
                                };
                              });
                              try {
                                await updateAdminContact(contact.id, {
                                  receives_marketing_emails: newValue,
                                  receives_booking_emails: newValue,
                                  receives_reminder_emails: newValue,
                                });
                              } catch {
                                setStudio(prev => {
                                  if (!prev) return prev;
                                  return {
                                    ...prev,
                                    contacts: prev.contacts.map(c =>
                                      c.id === contact.id
                                        ? { ...c, receives_marketing_emails: !newValue, receives_booking_emails: !newValue, receives_reminder_emails: !newValue }
                                        : c
                                    ),
                                  };
                                });
                              }
                            }}
                            className={`mt-2 py-1 px-3 text-xs font-medium rounded-lg border transition-colors ${
                              allOff
                                ? 'text-primary-600 border-primary-300 hover:bg-primary-50'
                                : 'text-danger-600 border-danger-300 hover:bg-danger-50'
                            }`}
                          >
                            {allOff ? 'Resubscribe to All Emails' : 'Unsubscribe from All Emails'}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bookings */}
          <div className="bg-white rounded-xl shadow-sm border border-warm-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-lg font-semibold text-warm-900">Bookings</h2>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warm-100 text-warm-600">
                {studio.bookings?.length ?? 0}
              </span>
              {(() => {
                // Outstanding = sum of balance still owed across bookings that
                // aren't paid/waived/cancelled. A waived/paid booking is settled
                // even if its raw balance_due_cents hasn't been zeroed.
                const totalDue = (studio.bookings ?? [])
                  .filter(
                    b =>
                      b.balance_due_cents > 0 &&
                      b.balance_status !== 'paid' &&
                      b.balance_status !== 'waived' &&
                      b.status !== 'cancelled'
                  )
                  .reduce((sum, b) => sum + b.balance_due_cents, 0);
                return totalDue > 0 ? (
                  <span className="ml-auto text-sm font-medium text-danger-600">
                    Total balance due: {formatCurrency(totalDue)}
                  </span>
                ) : null;
              })()}
            </div>

            {studio.bookings && studio.bookings.length > 0 ? (
              <div className="space-y-3">
                {studio.bookings.map(booking => {
                  const isSettled =
                    booking.balance_status === 'paid' || booking.balance_status === 'waived';
                  const owes =
                    booking.balance_due_cents > 0 &&
                    !isSettled &&
                    booking.status !== 'cancelled';
                  return (
                  <Link
                    key={booking.id}
                    to={`/admin/bookings/${booking.id}`}
                    className="block p-4 border border-warm-100 rounded-lg hover:bg-warm-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-warm-900">
                          {booking.show_name ? `${booking.show_name}` : `#${booking.booking_number}`}
                          {booking.city && <span className="text-warm-500 font-normal"> — {booking.city}, {booking.state}</span>}
                        </p>
                        <p className="text-xs text-warm-500 mt-0.5">
                          #{booking.booking_number}
                          {booking.class_date && <span> · Class: {formatDate(booking.class_date)}</span>}
                          <span> · Booked: {formatDate(booking.created_at)}</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={statusBadgeClass(booking.status)}>
                          {booking.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                        {booking.status === 'cancelled' ? null : owes ? (
                          <p className="text-xs font-medium text-danger-600 mt-1">
                            Balance due: {formatCurrency(booking.balance_due_cents)}
                            {booking.balance_due_date && (
                              <span className="text-warm-500 font-normal">
                                {' '}· Due {formatDate(booking.balance_due_date)}
                              </span>
                            )}
                          </p>
                        ) : booking.balance_status === 'waived' ? (
                          <p className="text-xs font-medium text-warm-500 mt-1">Waived</p>
                        ) : (
                          <p className="text-xs font-medium text-success-600 mt-1">
                            Balance paid
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-warm-400">No bookings yet</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-warm-100 p-6">
            <h2 className="text-lg font-semibold text-warm-900 mb-4">Quick Stats</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-warm-500">Total Bookings</p>
                {(studio.bookings?.length ?? studio.total_bookings) > 0 ? (
                  <Link
                    to={`/admin/bookings?studio_id=${studio.id}`}
                    className="text-2xl font-bold text-primary-600 hover:text-primary-700"
                  >
                    {studio.bookings?.length ?? studio.total_bookings}
                  </Link>
                ) : (
                  <p className="text-2xl font-bold text-warm-900">0</p>
                )}
              </div>
              <div>
                <p className="text-sm text-warm-500">Member Since</p>
                <p className="text-warm-900 font-medium">{formatDate(studio.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-warm-500">Stripe</p>
                {studio.stripe_customer_id ? (
                  <span className="inline-flex items-center gap-1.5 text-success-700 font-medium">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Connected
                  </span>
                ) : (
                  <span className="text-warm-500">Not connected</span>
                )}
              </div>
            </div>
          </div>

          {/* Campaign Targeting Cities */}
          <div className="bg-white rounded-xl shadow-sm border border-warm-100 p-6">
            <h2 className="text-lg font-semibold text-warm-900 mb-4">Campaign Cities</h2>
            {studio.cities.length === 0 ? (
              <p className="text-warm-500 text-sm">No cities linked</p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {studio.cities.map((city) => {
                    const isProximity = city.match_type === 'proximity';
                    return (
                      <span
                        key={city.id}
                        title={isProximity ? 'Nearby show market (within 50 mi) — receives this market’s campaigns' : 'Studio’s own city'}
                        className={`px-3 py-1 rounded-full text-sm ${isProximity ? 'bg-primary-50 text-primary-700 border border-primary-200' : 'bg-warm-100 text-warm-700'}`}
                      >
                        {city.name}, {city.state}
                        {isProximity && <span className="ml-1 text-xs opacity-70">· market</span>}
                      </span>
                    );
                  })}
                </div>
                {studio.cities.some((c) => c.match_type === 'proximity') && (
                  <p className="text-warm-500 text-xs mt-3">
                    "Market" cities are nearby show markets (within 50 mi) — this studio receives their show campaigns.
                  </p>
                )}
              </>
            )}
          </div>

          {/* SDA-299: tracked website visits */}
          <WebsiteVisitsCard studioId={studio.id} />
        </div>
      </div>

      {/* Send Activation Email confirmation (SDA-135) */}
      {showActivationConfirm && studio.primary_contact && (
        <ConfirmModal
          title="Send Activation Email"
          message={`Send an activation email to this studio's primary contact, ${studio.primary_contact.email}? Only the primary contact receives it. They'll be invited to set a password and claim this studio account.`}
          confirmLabel="Send Email"
          confirmVariant="primary"
          onConfirm={handleSendActivation}
          onCancel={() => setShowActivationConfirm(false)}
        />
      )}

      {/* Make Primary confirmation (SDA-208) */}
      {makePrimaryContact && (() => {
        const promotedName = `${makePrimaryContact.first_name} ${makePrimaryContact.last_name}`;
        const currentPrimary = studio.contacts.find(c => c.is_primary);
        const replaceClause = currentPrimary
          ? ` This replaces the current primary (${currentPrimary.first_name} ${currentPrimary.last_name})`
          : ' This sets the primary contact';
        return (
          <ConfirmModal
            title="Make Primary Contact"
            message={`Make ${promotedName} the primary contact?${replaceClause} and reroutes booking and inquiry emails to ${promotedName}.`}
            confirmLabel="Make Primary"
            confirmVariant="primary"
            onConfirm={() => handleMakePrimary(makePrimaryContact)}
            onCancel={() => setMakePrimaryContact(null)}
          />
        );
      })()}

      {/* Edit Contact Modal */}
      {editingContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-y-auto max-h-[90vh]">
            <div className="p-6 border-b border-warm-100">
              <h2 className="text-xl font-semibold text-warm-900">Edit Contact</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    First Name <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, first_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Last Name <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, last_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Email <span className="text-danger-500">*</span>
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, phone: formatPhoneDisplay(e.target.value) }))}
                  placeholder="(555) 555-5555"
                  className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Studio Director"
                  className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              {saveContactError && (
                <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
                  <p className="text-sm text-danger-700">{saveContactError}</p>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-warm-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditingContact(null)}
                disabled={isSavingContact}
                className="px-4 py-2 text-warm-700 hover:bg-warm-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveContact}
                disabled={isSavingContact || !editForm.first_name.trim() || !editForm.last_name.trim() || !editForm.email.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors font-medium"
              >
                {isSavingContact ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Contact Modal */}
      {isAddContactOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-warm-100">
              <h3 className="text-lg font-semibold text-warm-900">Add Contact</h3>
              <button
                type="button"
                onClick={() => setIsAddContactOpen(false)}
                className="text-warm-400 hover:text-warm-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleAddContact} className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    First Name <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={addForm.first_name}
                    onChange={e => setAddForm(f => ({ ...f, first_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 min-h-[44px]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Last Name <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={addForm.last_name}
                    onChange={e => setAddForm(f => ({ ...f, last_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 min-h-[44px]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Email <span className="text-danger-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={addForm.email}
                  onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 min-h-[44px]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={addForm.phone}
                  onChange={e => setAddForm(f => ({ ...f, phone: formatPhoneDisplay(e.target.value) }))}
                  placeholder="(XXX) XXX-XXXX"
                  className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 min-h-[44px]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">Title</label>
                <input
                  type="text"
                  value={addForm.title}
                  onChange={e => setAddForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-warm-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 min-h-[44px]"
                />
              </div>
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addForm.is_primary}
                    onChange={e => setAddForm(f => ({ ...f, is_primary: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>
                    <span className="text-sm font-medium text-warm-700">Set as Primary</span>
                    {addForm.is_primary && (
                      <span className="block text-xs text-warm-500 mt-0.5">Will demote the current primary contact</span>
                    )}
                  </span>
                </label>
              </div>
              {addContactError && (
                <p className="text-sm text-danger-600">{addContactError}</p>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsAddContactOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-warm-700 border border-warm-200 rounded-lg hover:bg-warm-50 transition-colors min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isAddingContact}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors min-h-[44px]"
                >
                  {isAddingContact ? 'Adding...' : 'Add Contact'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Studio Modal */}
      {showEditStudio && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSaveStudio} className="p-6">
              <h2 className="text-xl font-semibold text-warm-900 mb-4">Edit Studio</h2>

              {editStudioError && (
                <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
                  {editStudioError}
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">Studio Name *</label>
                  <input
                    type="text" value={editStudioForm.name}
                    onChange={(e) => setEditStudioForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">Address</label>
                  <input
                    type="text" value={editStudioForm.address_line1}
                    onChange={(e) => setEditStudioForm(f => ({ ...f, address_line1: e.target.value }))}
                    className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-warm-700 mb-1">City</label>
                    <PlacesAutocomplete
                      mode="city"
                      value={editStudioForm.city}
                      onInputChange={(textVal) => setEditStudioForm(f => ({ ...f, city: textVal, state: '', google_place_id: '' }))}
                      onPlaceSelected={(place: PlaceResult) => {
                        const cityName = place.addressComponents?.city || place.name;
                        setEditStudioForm(f => ({
                          ...f,
                          city: cityName || f.city,
                          state: place.addressComponents?.state || f.state,
                          google_place_id: place.placeId || '',
                        }));
                      }}
                      placeholder="Start typing a city…"
                      className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-700 mb-1">State</label>
                    <select
                      value={editStudioForm.state}
                      onChange={(e) => setEditStudioForm(f => ({ ...f, state: e.target.value, google_place_id: '' }))}
                      className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">—</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-700 mb-1">ZIP *</label>
                    <input
                      type="text" value={editStudioForm.zip_code}
                      onChange={(e) => setEditStudioForm(f => ({ ...f, zip_code: e.target.value }))}
                      className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-warm-700 mb-1">Phone</label>
                    <input
                      type="tel" value={editStudioForm.phone}
                      onChange={(e) => setEditStudioForm(f => ({ ...f, phone: formatPhoneDisplay(e.target.value) }))}
                      className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-700 mb-1">Website</label>
                    <input
                      type="text" value={editStudioForm.website}
                      onChange={(e) => setEditStudioForm(f => ({ ...f, website: e.target.value }))}
                      className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditStudio(false)}
                  className="px-4 py-2 text-warm-600 hover:text-warm-900 transition-colors"
                  disabled={isSavingStudio}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSavingStudio}>
                  {isSavingStudio ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Studio Confirmation Modal (SDA-138) */}
      {showDelete && studio && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-warm-900">Delete Studio</h3>
            <p className="text-sm text-warm-600 mt-2">
              This permanently deletes <span className="font-medium text-warm-900">{studio.name}</span> and
              its contacts. This can't be undone. Studios with bookings, payments, or inquiries can't be
              deleted — resolve those first.
            </p>

            {deleteError && (
              <div className="mt-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
                {deleteError}
              </div>
            )}

            <label className="block text-sm text-warm-700 mt-4 mb-1">
              Type the studio name to confirm:
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-danger-500 focus:border-danger-500"
              placeholder={studio.name}
            />

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowDelete(false); setDeleteConfirmText(''); setDeleteError(null); }}
                className="px-4 py-2 text-warm-600 hover:text-warm-900 transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteStudio}
                disabled={isDeleting || deleteConfirmText.trim() !== studio.name}
                className="px-4 py-2 text-sm font-medium text-white bg-danger-600 rounded-lg hover:bg-danger-700 disabled:opacity-50 transition-colors"
              >
                {isDeleting ? 'Deleting...' : 'Delete Studio'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
